import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { loadYouTubeApi } from "../../../../utils/youtube-iframe-api";

const PLAYING_STATE = 1;
const PAUSED_STATE = 2;
const ENDED_STATE = 0;
const PLAYER_CLASS = "Add-Exercise-videoPreview-player";

/**
 * Inline YouTube player the preview can play and pause from its own button.
 *
 * Built with the IFrame API (same script the duration lookup uses) so the card gets
 * `playVideo` / `pauseVideo` and state events. When the API cannot load, it falls back to a
 * plain iframe: the video still plays with YouTube's own controls, and `onFailed` tells the
 * card to hide its Play/Pause button.
 *
 * @param {{
 *   videoId: string,
 *   fallbackUrl: string,
 *   onPlayerChange: (player: { play: () => void, pause: () => void } | null) => void,
 *   onPlayingChange: (playing: boolean) => void,
 *   onFailed: () => void,
 * }} props
 */
function YouTubeInlinePlayer({
  videoId,
  fallbackUrl,
  onPlayerChange,
  onPlayingChange,
  onFailed,
}) {
  const { t } = useTranslation("coaching-portal");
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const onPlayerChangeRef = useRef(onPlayerChange);
  onPlayerChangeRef.current = onPlayerChange;
  const onPlayingChangeRef = useRef(onPlayingChange);
  onPlayingChangeRef.current = onPlayingChange;
  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;
  const title = t("addExerciseModal.videoPreview.playerTitle");

  useEffect(() => {
    let cancelled = false;
    let player = null;

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !containerRef.current) {
          return;
        }
        const host = document.createElement("div");
        containerRef.current.appendChild(host);
        player = new YT.Player(host, {
          width: "100%",
          height: "100%",
          videoId,
          playerVars: { autoplay: 1, rel: 0, playsinline: 1 },
          events: {
            onReady: (event) => {
              const iframe = event.target.getIframe();
              iframe.className = PLAYER_CLASS;
              iframe.title = title;
              onPlayerChangeRef.current({
                play: () => event.target.playVideo(),
                pause: () => event.target.pauseVideo(),
              });
              event.target.playVideo();
            },
            onStateChange: (event) => {
              if (event.data === PLAYING_STATE) {
                onPlayingChangeRef.current(true);
              } else if (
                event.data === PAUSED_STATE ||
                event.data === ENDED_STATE
              ) {
                onPlayingChangeRef.current(false);
              }
            },
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          onFailedRef.current();
        }
      });

    return () => {
      cancelled = true;
      onPlayerChangeRef.current(null);
      onPlayingChangeRef.current(false);
      try {
        player?.destroy?.();
      } catch (error) {
        console.error("[YouTubeInlinePlayer] could not destroy player", error);
      }
    };
  }, [videoId, title]);

  if (failed) {
    return (
      <iframe
        className={PLAYER_CLASS}
        src={fallbackUrl}
        title={title}
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
      />
    );
  }

  return <div ref={containerRef} className={PLAYER_CLASS} />;
}

YouTubeInlinePlayer.propTypes = {
  videoId: PropTypes.string.isRequired,
  fallbackUrl: PropTypes.string.isRequired,
  onPlayerChange: PropTypes.func.isRequired,
  onPlayingChange: PropTypes.func.isRequired,
  onFailed: PropTypes.func.isRequired,
};

export default YouTubeInlinePlayer;
